<template>
  <div class="rack-optimization-calculator" data-theme="v1">
    <!-- Main Content -->
    <div class="rack-optimization-calculator__container">
      <!-- Page Header -->
      <div class="rack-optimization-calculator__page-header">
        <div class="rack-optimization-calculator__header-content">
          <div class="rack-optimization-calculator__header-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
            </svg>
          </div>
          <div>
            <h2 class="rack-optimization-calculator__page-title">Cubic Feet + Rack Optimization Calculator</h2>
            <p class="rack-optimization-calculator__page-description">Calculate total cubic feet, usable storage, and rack configuration optimization</p>
          </div>
        </div>
      </div>

      <!-- Calculator Form -->
      <div class="rack-optimization-calculator__form-section">
        <!-- Mode Toggle -->
        <div class="calculator-mode-toggle">
          <div class="mode-toggle__label">Calculation Mode:</div>
          <div class="mode-toggle__buttons">
            <themed-button
              type="button"
              variant="outline"
              :class="['mode-toggle__button', { 'mode-toggle__button--active': calculationMode === 'building-to-storage' }]"
              @click="switchMode('building-to-storage')"
            >
              <svg class="mode-toggle__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
              <span>Building → Storage</span>
            </themed-button>
            <themed-button
              type="button"
              variant="outline"
              :class="['mode-toggle__button', { 'mode-toggle__button--active': calculationMode === 'storage-to-building' }]"
              @click="switchMode('storage-to-building')"
            >
              <svg class="mode-toggle__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
              <span>Storage → Building</span>
            </themed-button>
          </div>
          <div class="mode-toggle__description">
            <span v-if="calculationMode === 'building-to-storage'">
              Calculate how much storage you can achieve with your building dimensions
            </span>
            <span v-else>
              Calculate what building footprint you need for your storage requirements
            </span>
          </div>
        </div>

        <!-- Section A: Building Dimensions or Storage Requirements -->
        <div class="calculator-section">
          <h3 class="calculator-section__title">
            <span v-if="calculationMode === 'building-to-storage'">A. Building Dimensions (Feet)</span>
            <span v-else>A. Storage Requirements</span>
          </h3>
          <themed-form
            ref="sectionAForm"
            :fields="calculationMode === 'building-to-storage' ? buildingToStorageFields : storageToBuildingFields"
            :model-value="formData"
            layout="grid"
            :columns="3"
            :validate-on-change="false"
            @input="handleFormInput"
          />
        </div>

        <!-- Section B: Rack Configuration -->
        <div class="calculator-section">
          <h3 class="calculator-section__title">B. Rack Configuration</h3>
          <themed-form
            ref="sectionBForm"
            :fields="rackConfigFields"
            :model-value="formData"
            layout="grid"
            :columns="3"
            :validate-on-change="false"
            @input="handleFormInput"
          />
        </div>

        <!-- Section C: Equipment Type -->
        <div class="calculator-section">
          <h3 class="calculator-section__title">C. Material Handling Equipment</h3>
          <themed-form
            ref="sectionCForm"
            :fields="equipmentFields"
            :model-value="formData"
            layout="grid"
            :columns="1"
            :validate-on-change="false"
            @input="handleFormInput"
          />
        </div>

        <!-- Calculate Button -->
        <div class="calculator-actions">
          <themed-button type="button" variant="default" size="lg" class="btn-calculate" @click="calculate">
            <svg class="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
            </svg>
            Calculate Storage Optimization
          </themed-button>
          <themed-button type="button" variant="outline" size="lg" class="btn-reset" @click="reset">
            Reset
          </themed-button>
        </div>
      </div>

      <!-- Results Section -->
      <div v-if="results" class="rack-optimization-calculator__results">
        <h3 class="results-title">
          <span v-if="calculationMode === 'building-to-storage'">Storage Optimization Results</span>
          <span v-else>Required Building Footprint</span>
        </h3>
        
        <!-- Summary Cards -->
        <div class="results-grid">
          <!-- Building Volume Card -->
          <div class="result-card result-card--primary">
            <div class="result-card__icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">
                <span v-if="calculationMode === 'building-to-storage'">Building Volume</span>
                <span v-else>Required Building Footprint</span>
              </div>
              <div class="result-card__value">
                <span v-if="calculationMode === 'building-to-storage'">{{ formatNumber(results.buildingVolume) }} ft³</span>
                <span v-else>{{ formatNumber(results.buildingFootprint) }} ft²</span>
              </div>
              <div v-if="calculationMode === 'storage-to-building'" class="result-card__formula">
                {{ formatNumber(buildingLength) }} ft × {{ formatNumber(results.buildingWidth) }} ft
              </div>
            </div>
          </div>

          <!-- Storage Volume Card -->
          <div class="result-card">
            <div class="result-card__icon result-card__icon--success">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">
                <span v-if="calculationMode === 'building-to-storage'">Usable Storage Volume</span>
                <span v-else>Achieved Storage Volume</span>
              </div>
              <div class="result-card__value">{{ formatNumber(results.storageVolume) }} ft³</div>
              <div class="result-card__formula">{{ results.numberOfRows }} rows × {{ formatNumber(results.totalLinearFeet) }} ft × {{ formatNumber(results.rackDepthFt) }} ft × {{ formatNumber(results.rackHeightFt) }} ft × {{ results.rackLevels }} levels</div>
            </div>
          </div>

          <!-- Utilization or Building Width Card -->
          <div class="result-card">
            <div class="result-card__icon result-card__icon--info">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="calculationMode === 'building-to-storage'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">
                <span v-if="calculationMode === 'building-to-storage'">Storage Utilization</span>
                <span v-else>Required Building Width</span>
              </div>
              <div class="result-card__value">
                <span v-if="calculationMode === 'building-to-storage'">{{ formatNumber(results.utilizationPercent) }}%</span>
                <span v-else>{{ formatNumber(results.buildingWidth) }} ft</span>
              </div>
              <div class="result-card__formula">
                <span v-if="calculationMode === 'building-to-storage'">Storage Volume ÷ Building Volume</span>
                <span v-else>{{ results.numberOfRows }} rows × {{ formatNumber(results.laneWidth) }} ft/row</span>
              </div>
            </div>
          </div>

          <!-- Number of Rows Card -->
          <div class="result-card">
            <div class="result-card__icon result-card__icon--purple">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"></path>
              </svg>
            </div>
            <div class="result-card__content">
              <div class="result-card__label">Number of Rack Rows</div>
              <div class="result-card__value">{{ results.numberOfRows }}</div>
              <div class="result-card__formula">{{ formatNumber(results.totalLinearFeet) }} total linear feet</div>
            </div>
          </div>
        </div>

        <!-- Intermediate Calculations -->
        <div class="results-breakdown">
          <h4 class="breakdown-title">Configuration Details</h4>
          <div class="breakdown-grid">
            <div class="breakdown-item">
              <span class="breakdown-label">Rack Height:</span>
              <span class="breakdown-value">{{ rackHeight }}" ({{ formatNumber(results.rackHeightFt) }} ft)</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Rack Depth:</span>
              <span class="breakdown-value">{{ rackDepth }}" ({{ formatNumber(results.rackDepthFt) }} ft)</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Rack Levels:</span>
              <span class="breakdown-value">{{ results.rackLevels }} level{{ results.rackLevels !== 1 ? 's' : '' }}</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Equipment Type:</span>
              <span class="breakdown-value">{{ getEquipmentLabel(equipmentType) }}</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Required Aisle Width:</span>
              <span class="breakdown-value">{{ results.aisleWidth }} ft</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Lane Width (Rack + Aisle):</span>
              <span class="breakdown-value">{{ formatNumber(results.laneWidth) }} ft</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Rack Run Length:</span>
              <span class="breakdown-value">{{ formatNumber(rackRunLength) }} ft</span>
            </div>
          </div>
        </div>

        <!-- Equipment Comparison -->
        <div v-if="comparisonResults.length > 0" class="results-comparison">
          <h4 class="breakdown-title">Equipment Comparison</h4>
          <p class="comparison-description">
            <span v-if="calculationMode === 'building-to-storage'">See how different equipment choices impact your storage capacity</span>
            <span v-else>See how different equipment choices impact required building width</span>
          </p>
          
          <div class="comparison-table">
            <div class="comparison-header">
              <div class="comparison-cell">Equipment</div>
              <div class="comparison-cell">Aisle Width</div>
              <div class="comparison-cell">Rows</div>
              <div class="comparison-cell" v-if="calculationMode === 'building-to-storage'">Storage Volume</div>
              <div class="comparison-cell" v-else>Building Width</div>
              <div class="comparison-cell" v-if="calculationMode === 'building-to-storage'">Utilization</div>
              <div class="comparison-cell" v-else>Footprint</div>
              <div class="comparison-cell">vs Current</div>
            </div>
            <div 
              v-for="comp in comparisonResults" 
              :key="comp.equipment"
              :class="['comparison-row', { 'comparison-row--current': comp.equipment === equipmentType }]"
            >
              <div class="comparison-cell">
                <span class="comparison-equipment">{{ getEquipmentLabel(comp.equipment) }}</span>
              </div>
              <div class="comparison-cell">{{ comp.aisleWidth }} ft</div>
              <div class="comparison-cell">{{ comp.numberOfRows }}</div>
              <div class="comparison-cell" v-if="calculationMode === 'building-to-storage'">{{ formatNumber(comp.storageVolume) }} ft³</div>
              <div class="comparison-cell" v-else>{{ formatNumber(comp.buildingWidth) }} ft</div>
              <div class="comparison-cell" v-if="calculationMode === 'building-to-storage'">{{ formatNumber(comp.utilizationPercent) }}%</div>
              <div class="comparison-cell" v-else>{{ formatNumber(comp.buildingFootprint) }} ft²</div>
              <div class="comparison-cell">
                <span :class="['comparison-diff', comp.diffClass]">
                  {{ comp.diffLabel }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Modal -->
    <email-modal
      :show="showEmailModal"
      @update:show="showEmailModal = $event"
      @submit="handleEmailSubmit"
      @cancel="handleEmailCancel"
      title="Enter Your Email"
      description="Please provide your email address to view the calculation results."
      calculation="rack-optimization-calculator"
    />
  </div>
</template>

<script>
import EmailModal from '../../../common/EmailModal.vue';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedButton from '../ThemedButton.vue';

export default {
  name: 'RackOptimizationCalculator',

  components: {
    EmailModal,
    ThemedForm,
    ThemedButton
  },

  data() {
    return {
      // Calculation Mode
      calculationMode: 'building-to-storage', // 'building-to-storage' or 'storage-to-building'
      
      // Form data object for ThemedForm
      formData: {
        buildingLength: null,
        buildingWidth: null,
        ceilingHeight: null,
        targetStorageVolume: null,
        rackHeight: '',
        rackDepth: '',
        rackRunLength: null,
        rackLevelsMode: 'automatic',
        rackLevels: 1,
        equipmentType: ''
      },
      
      results: null,
      comparisonResults: [],
      showEmailModal: false,
      userEmail: null,
      pendingCalculation: false,
      
      // Equipment aisle width mapping
      equipmentAisleWidths: {
        manual: 6,
        electric: 8,
        reach: 9,
        forklift: 12
      }
    };
  },

  computed: {
    // Computed properties for backward compatibility
    buildingLength: {
      get() { return this.formData.buildingLength; },
      set(val) { this.formData.buildingLength = val; }
    },
    buildingWidth: {
      get() { return this.formData.buildingWidth; },
      set(val) { this.formData.buildingWidth = val; }
    },
    ceilingHeight: {
      get() { return this.formData.ceilingHeight; },
      set(val) { this.formData.ceilingHeight = val; }
    },
    targetStorageVolume: {
      get() { return this.formData.targetStorageVolume; },
      set(val) { this.formData.targetStorageVolume = val; }
    },
    rackHeight: {
      get() { return this.formData.rackHeight; },
      set(val) { this.formData.rackHeight = val; }
    },
    rackDepth: {
      get() { return this.formData.rackDepth; },
      set(val) { this.formData.rackDepth = val; }
    },
    rackRunLength: {
      get() { return this.formData.rackRunLength; },
      set(val) { this.formData.rackRunLength = val; }
    },
    rackLevelsMode: {
      get() { return this.formData.rackLevelsMode; },
      set(val) { this.formData.rackLevelsMode = val; }
    },
    rackLevels: {
      get() { return this.formData.rackLevels; },
      set(val) { this.formData.rackLevels = val; }
    },
    equipmentType: {
      get() { return this.formData.equipmentType; },
      set(val) { this.formData.equipmentType = val; }
    },

    // Form field configurations
    buildingToStorageFields() {
      return [
        {
          name: 'buildingLength',
          type: 'input',
          inputType: 'number',
          label: 'Building Length (ft)',
          placeholder: 'e.g., 200',
          helpText: 'Total length of the building',
          required: true,
          col: 1,
          componentProps: {
            min: 0,
            step: 0.01
          },
          validation: {
            required: true,
            customFn: (value) => {
              if (!value || value <= 0) return 'Building length must be greater than 0';
              return true;
            }
          }
        },
        {
          name: 'buildingWidth',
          type: 'input',
          inputType: 'number',
          label: 'Building Width (ft)',
          placeholder: 'e.g., 150',
          helpText: 'Total width of the building',
          required: true,
          col: 1,
          componentProps: {
            min: 0,
            step: 0.01
          },
          validation: {
            required: true,
            customFn: (value) => {
              if (!value || value <= 0) return 'Building width must be greater than 0';
              return true;
            }
          }
        },
        {
          name: 'ceilingHeight',
          type: 'input',
          inputType: 'number',
          label: 'Ceiling Height (ft)',
          placeholder: 'e.g., 24',
          helpText: 'Clear ceiling height',
          required: true,
          col: 1,
          componentProps: {
            min: 0,
            step: 0.01
          },
          validation: {
            required: true,
            customFn: (value) => {
              if (!value || value <= 0) return 'Ceiling height must be greater than 0';
              return true;
            }
          }
        }
      ];
    },

    storageToBuildingFields() {
      return [
        {
          name: 'targetStorageVolume',
          type: 'input',
          inputType: 'number',
          label: 'Target Storage Volume (ft³)',
          placeholder: 'e.g., 50000',
          helpText: 'Desired cubic feet of usable storage',
          required: true,
          col: 1,
          componentProps: {
            min: 0,
            step: 0.01
          },
          validation: {
            required: true,
            customFn: (value) => {
              if (!value || value <= 0) return 'Target storage volume must be greater than 0';
              return true;
            }
          }
        },
        {
          name: 'buildingLength',
          type: 'input',
          inputType: 'number',
          label: 'Building Length (ft)',
          placeholder: 'e.g., 200',
          helpText: 'Fixed building length (we\'ll calculate required width)',
          required: true,
          col: 1,
          componentProps: {
            min: 0,
            step: 0.01
          },
          validation: {
            required: true,
            customFn: (value) => {
              if (!value || value <= 0) return 'Building length must be greater than 0';
              return true;
            }
          }
        },
        {
          name: 'ceilingHeight',
          type: 'input',
          inputType: 'number',
          label: 'Ceiling Height (ft)',
          placeholder: 'e.g., 24',
          helpText: 'Clear ceiling height',
          required: true,
          col: 1,
          componentProps: {
            min: 0,
            step: 0.01
          },
          validation: {
            required: true,
            customFn: (value) => {
              if (!value || value <= 0) return 'Ceiling height must be greater than 0';
              return true;
            }
          }
        }
      ];
    },

    rackConfigFields() {
      return [
        {
          name: 'rackHeight',
          type: 'select',
          label: 'Rack Height',
          placeholder: 'Select height...',
          helpText: 'Height of rack units',
          required: true,
          col: 1,
          options: [
            { label: '48 inches (4 ft)', value: 48 },
            { label: '60 inches (5 ft)', value: 60 },
            { label: '72 inches (6 ft)', value: 72 }
          ],
          validation: {
            required: true,
            customFn: (value) => {
              if (!value) return 'Please select a rack height';
              return true;
            }
          }
        },
        {
          name: 'rackDepth',
          type: 'select',
          label: 'Rack Depth',
          placeholder: 'Select depth...',
          helpText: 'Depth of rack units',
          required: true,
          col: 1,
          options: [
            { label: '36 inches (3 ft)', value: 36 },
            { label: '40 inches (3.33 ft)', value: 40 },
            { label: '48 inches (4 ft)', value: 48 },
            { label: '60 inches (5 ft)', value: 60 }
          ],
          validation: {
            required: true,
            customFn: (value) => {
              if (!value) return 'Please select a rack depth';
              return true;
            }
          }
        },
        {
          name: 'rackRunLength',
          type: 'input',
          inputType: 'number',
          label: 'Rack Run Length (ft)',
          placeholder: 'e.g., 100',
          helpText: 'Linear feet per rack row',
          required: true,
          col: 1,
          componentProps: {
            min: 0,
            step: 0.01
          },
          validation: {
            required: true,
            customFn: (value) => {
              if (!value || value <= 0) return 'Rack run length must be greater than 0';
              return true;
            }
          }
        },
        {
          name: 'rackLevelsMode',
          type: 'select',
          label: 'Rack Levels Mode',
          helpText: 'How to determine number of rack levels',
          required: true,
          col: 1,
          options: [
            { label: 'Automatic (based on ceiling height)', value: 'automatic' },
            { label: 'Manual Entry', value: 'manual' }
          ],
          validation: {
            required: true
          }
        },
        {
          name: 'rackLevels',
          type: 'input',
          inputType: 'number',
          label: 'Number of Rack Levels',
          placeholder: 'e.g., 4',
          helpText: this.rackLevelsHelpText,
          required: true,
          col: 1,
          componentProps: {
            min: 1,
            step: 1,
            readonly: this.formData.rackLevelsMode === 'automatic'
          },
          validation: {
            required: true,
            customFn: (value) => {
              if (!value || value < 1) return 'Rack levels must be at least 1';
              return true;
            }
          }
        }
      ];
    },

    equipmentFields() {
      return [
        {
          name: 'equipmentType',
          type: 'select',
          label: 'Equipment Type',
          placeholder: 'Select equipment...',
          helpText: 'Equipment determines required aisle width',
          required: true,
          col: 1,
          options: [
            { label: 'Manual Pallet Jack (6 ft aisle)', value: 'manual' },
            { label: 'Electric Pallet Jack (8 ft aisle)', value: 'electric' },
            { label: 'Reach Truck (9 ft aisle)', value: 'reach' },
            { label: 'Standard Forklift (12 ft aisle)', value: 'forklift' }
          ],
          validation: {
            required: true,
            customFn: (value) => {
              if (!value) return 'Please select an equipment type';
              return true;
            }
          }
        }
      ];
    },

    rackLevelsHelpText() {
      if (this.formData.rackLevelsMode === 'automatic') {
        const ceiling = this.formData.ceilingHeight || 0;
        const rackHeightFt = (this.formData.rackHeight || 0) / 12;
        const levels = this.formData.rackLevels || 0;
        return `Auto-calculated: FLOOR(${ceiling} ft ÷ ${this.formatNumber(rackHeightFt)} ft) = ${levels} levels`;
      }
      return 'Number of vertical levels in each rack';
    }
  },

  mounted() {
    // Check if user info is already stored
    const storedEmail = sessionStorage.getItem('public_view_email');
    const storedName = sessionStorage.getItem('public_view_name');
    const storedPhone = sessionStorage.getItem('public_view_phone');
    
    if (storedEmail) {
      this.userEmail = storedEmail;
      
      // Update header with stored user info
      if (this.$refs.header) {
        this.$refs.header.userEmail = storedEmail;
        if (storedName) {
          this.$refs.header.userName = storedName;
        }
        if (storedPhone) {
          this.$refs.header.userPhone = storedPhone;
        }
      }
    }
  },

  watch: {
    'formData.ceilingHeight'() {
      if (this.formData.rackLevelsMode === 'automatic') {
        this.updateRackLevels();
      }
    },
    'formData.rackHeight'() {
      if (this.formData.rackLevelsMode === 'automatic') {
        this.updateRackLevels();
      }
    },
    'formData.rackLevelsMode'() {
      this.updateRackLevels();
    }
  },

  methods: {
    handleFormInput(updatedData) {
      this.formData = updatedData;
    },
    updateRackLevels() {
      if (this.formData.rackLevelsMode === 'automatic' && this.formData.ceilingHeight && this.formData.rackHeight) {
        const ceilingHeightFt = this.formData.ceilingHeight;
        const rackHeightFt = this.formData.rackHeight / 12;
        this.formData.rackLevels = Math.floor(ceilingHeightFt / rackHeightFt);
        
        // Ensure at least 1 level
        if (this.formData.rackLevels < 1) {
          this.formData.rackLevels = 1;
        }
      }
    },

    switchMode(mode) {
      this.calculationMode = mode;
      this.reset();
    },

    calculate() {
      // Validate all forms first
      const sectionAValid = this.$refs.sectionAForm ? this.$refs.sectionAForm.validateAllFields() : false;
      const sectionBValid = this.$refs.sectionBForm ? this.$refs.sectionBForm.validateAllFields() : false;
      const sectionCValid = this.$refs.sectionCForm ? this.$refs.sectionCForm.validateAllFields() : false;

      if (!sectionAValid || !sectionBValid || !sectionCValid) {
        // Validation failed, errors are already displayed by ThemedForm
        return;
      }

      // Check if user is authenticated
      const isAuthenticated = window.user && window.user.id;
      
      // If not authenticated and no email, show email modal first
      if (!isAuthenticated && !this.userEmail) {
        // Show email modal first
        this.pendingCalculation = true;
        this.showEmailModal = true;
        return;
      }

      // Perform calculation based on mode
      if (this.calculationMode === 'building-to-storage') {
        this.performForwardCalculation();
      } else {
        this.performReverseCalculation();
      }
    },

    performForwardCalculation() {
      // Step 1: Building Volume
      const buildingVolume = (this.buildingLength || 0) * (this.buildingWidth || 0) * (this.ceilingHeight || 0);
      
      // Step 2: Convert Rack Dimensions
      const rackHeightFt = (this.rackHeight || 0) / 12;
      const rackDepthFt = (this.rackDepth || 0) / 12;
      
      // Step 3: Determine Lane Width
      const aisleWidth = this.equipmentAisleWidths[this.equipmentType] || 0;
      const laneWidth = rackDepthFt + aisleWidth;
      
      // Step 4: Calculate Rack Rows
      const numberOfRows = Math.floor((this.buildingWidth || 0) / laneWidth);
      
      // Step 5: Total Linear Racking
      const totalLinearFeet = numberOfRows * (this.rackRunLength || 0);
      
      // Step 6: Usable Storage Volume (now includes rack levels)
      const rackLevels = this.rackLevels || 1;
      const storageVolume = totalLinearFeet * rackDepthFt * rackHeightFt * rackLevels;
      
      // Step 7: Utilization %
      const utilizationPercent = buildingVolume > 0 ? (storageVolume / buildingVolume) * 100 : 0;
      
      this.results = {
        buildingVolume,
        storageVolume,
        utilizationPercent,
        numberOfRows,
        totalLinearFeet,
        rackHeightFt,
        rackDepthFt,
        aisleWidth,
        laneWidth,
        rackLevels
      };
      
      // Generate comparison results
      this.generateComparison();
    },

    performReverseCalculation() {
      // Given: Target storage volume, rack config, equipment, building length
      // Calculate: Required building width and footprint
      
      // Step 1: Convert Rack Dimensions
      const rackHeightFt = (this.rackHeight || 0) / 12;
      const rackDepthFt = (this.rackDepth || 0) / 12;
      
      // Step 2: Determine Lane Width
      const aisleWidth = this.equipmentAisleWidths[this.equipmentType] || 0;
      const laneWidth = rackDepthFt + aisleWidth;
      
      // Step 3: Calculate Required Number of Rows
      // Storage Volume = Total Linear Feet × Rack Depth × Rack Height × Rack Levels
      // Total Linear Feet = Number of Rows × Run Length
      // Storage Volume = Number of Rows × Run Length × Rack Depth × Rack Height × Rack Levels
      // Number of Rows = Storage Volume ÷ (Run Length × Rack Depth × Rack Height × Rack Levels)
      
      const targetVolume = this.targetStorageVolume || 0;
      const runLength = this.rackRunLength || 0;
      const rackLevels = this.rackLevels || 1;
      
      if (runLength === 0 || rackDepthFt === 0 || rackHeightFt === 0 || rackLevels === 0) {
        alert('Please ensure all rack configuration values are greater than zero.');
        return;
      }
      
      const numberOfRows = Math.ceil(targetVolume / (runLength * rackDepthFt * rackHeightFt * rackLevels));
      
      // Step 4: Calculate Required Building Width
      // Building Width = Number of Rows × Lane Width
      const buildingWidth = numberOfRows * laneWidth;
      
      // Step 5: Calculate Total Linear Feet
      const totalLinearFeet = numberOfRows * runLength;
      
      // Step 6: Calculate Actual Storage Volume (may be slightly more than target due to rounding up rows)
      const storageVolume = totalLinearFeet * rackDepthFt * rackHeightFt * rackLevels;
      
      // Step 7: Calculate Building Volume and Footprint
      const buildingVolume = (this.buildingLength || 0) * buildingWidth * (this.ceilingHeight || 0);
      const buildingFootprint = (this.buildingLength || 0) * buildingWidth;
      
      // Step 8: Utilization %
      const utilizationPercent = buildingVolume > 0 ? (storageVolume / buildingVolume) * 100 : 0;
      
      // Store calculated building width for display
      this.buildingWidth = buildingWidth;
      
      this.results = {
        buildingVolume,
        storageVolume,
        utilizationPercent,
        numberOfRows,
        totalLinearFeet,
        rackHeightFt,
        rackDepthFt,
        aisleWidth,
        laneWidth,
        buildingWidth,
        buildingFootprint,
        rackLevels,
        isReverseCalculation: true
      };
      
      // Generate comparison results
      this.generateComparisonReverse();
    },

    generateComparison() {
      const equipmentTypes = ['manual', 'electric', 'reach', 'forklift'];
      const currentStorageVolume = this.results.storageVolume;
      const rackLevels = this.rackLevels || 1;
      
      this.comparisonResults = equipmentTypes.map(equipment => {
        const aisleWidth = this.equipmentAisleWidths[equipment];
        const rackDepthFt = (this.rackDepth || 0) / 12;
        const rackHeightFt = (this.rackHeight || 0) / 12;
        const laneWidth = rackDepthFt + aisleWidth;
        const numberOfRows = Math.floor((this.buildingWidth || 0) / laneWidth);
        const totalLinearFeet = numberOfRows * (this.rackRunLength || 0);
        const storageVolume = totalLinearFeet * rackDepthFt * rackHeightFt * rackLevels;
        const buildingVolume = (this.buildingLength || 0) * (this.buildingWidth || 0) * (this.ceilingHeight || 0);
        const utilizationPercent = buildingVolume > 0 ? (storageVolume / buildingVolume) * 100 : 0;
        
        // Calculate difference
        const diff = storageVolume - currentStorageVolume;
        const diffPercent = currentStorageVolume > 0 ? (diff / currentStorageVolume) * 100 : 0;
        
        let diffClass = '';
        let diffLabel = '';
        
        if (equipment === this.equipmentType) {
          diffClass = 'comparison-diff--current';
          diffLabel = 'Current';
        } else if (diff > 0) {
          diffClass = 'comparison-diff--positive';
          diffLabel = `+${this.formatNumber(Math.abs(diffPercent))}%`;
        } else if (diff < 0) {
          diffClass = 'comparison-diff--negative';
          diffLabel = `-${this.formatNumber(Math.abs(diffPercent))}%`;
        } else {
          diffClass = 'comparison-diff--neutral';
          diffLabel = '0%';
        }
        
        return {
          equipment,
          aisleWidth,
          numberOfRows,
          storageVolume,
          utilizationPercent,
          diffClass,
          diffLabel
        };
      });
    },

    generateComparisonReverse() {
      const equipmentTypes = ['manual', 'electric', 'reach', 'forklift'];
      const targetVolume = this.targetStorageVolume || 0;
      const rackLevels = this.rackLevels || 1;
      
      this.comparisonResults = equipmentTypes.map(equipment => {
        const aisleWidth = this.equipmentAisleWidths[equipment];
        const rackDepthFt = (this.rackDepth || 0) / 12;
        const rackHeightFt = (this.rackHeight || 0) / 12;
        const laneWidth = rackDepthFt + aisleWidth;
        const runLength = this.rackRunLength || 0;
        
        const numberOfRows = Math.ceil(targetVolume / (runLength * rackDepthFt * rackHeightFt * rackLevels));
        const buildingWidth = numberOfRows * laneWidth;
        const totalLinearFeet = numberOfRows * runLength;
        const storageVolume = totalLinearFeet * rackDepthFt * rackHeightFt * rackLevels;
        const buildingVolume = (this.buildingLength || 0) * buildingWidth * (this.ceilingHeight || 0);
        const utilizationPercent = buildingVolume > 0 ? (storageVolume / buildingVolume) * 100 : 0;
        const buildingFootprint = (this.buildingLength || 0) * buildingWidth;
        
        // Calculate difference in building width
        const widthDiff = buildingWidth - this.results.buildingWidth;
        const widthDiffPercent = this.results.buildingWidth > 0 ? (widthDiff / this.results.buildingWidth) * 100 : 0;
        
        let diffClass = '';
        let diffLabel = '';
        
        if (equipment === this.equipmentType) {
          diffClass = 'comparison-diff--current';
          diffLabel = 'Current';
        } else if (widthDiff < 0) {
          diffClass = 'comparison-diff--positive';
          diffLabel = `${this.formatNumber(Math.abs(widthDiffPercent))}% less width`;
        } else if (widthDiff > 0) {
          diffClass = 'comparison-diff--negative';
          diffLabel = `+${this.formatNumber(Math.abs(widthDiffPercent))}% more width`;
        } else {
          diffClass = 'comparison-diff--neutral';
          diffLabel = 'Same';
        }
        
        return {
          equipment,
          aisleWidth,
          numberOfRows,
          storageVolume,
          utilizationPercent,
          buildingWidth,
          buildingFootprint,
          diffClass,
          diffLabel
        };
      });
    },

    reset() {
      this.formData = {
        buildingLength: null,
        buildingWidth: null,
        ceilingHeight: null,
        targetStorageVolume: null,
        rackHeight: '',
        rackDepth: '',
        rackRunLength: null,
        rackLevelsMode: 'automatic',
        rackLevels: 1,
        equipmentType: ''
      };
      this.results = null;
      this.comparisonResults = [];
    },

    handleEmailSubmit(userInfo) {
      this.userEmail = userInfo.email;
      this.showEmailModal = false;
      
      // Update the header component with the user info
      if (this.$refs.header) {
        this.$refs.header.userEmail = userInfo.email;
        this.$refs.header.userName = userInfo.fullName;
        this.$refs.header.userPhone = userInfo.phone;
      }
      
      // If calculation was pending, perform it now
      if (this.pendingCalculation) {
        this.pendingCalculation = false;
        // Perform calculation based on mode
        if (this.calculationMode === 'building-to-storage') {
          this.performForwardCalculation();
        } else {
          this.performReverseCalculation();
        }
      }
    },

    handleEmailCancel() {
      this.pendingCalculation = false;
      this.showEmailModal = false;
    },

    getEquipmentLabel(equipment) {
      const labels = {
        manual: 'Manual Pallet Jack',
        electric: 'Electric Pallet Jack',
        reach: 'Reach Truck',
        forklift: 'Standard Forklift'
      };
      return labels[equipment] || equipment;
    },

    formatNumber(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
  }
};
</script>
